<template>
    <!-- Default rendering: the native <select> this has always used. The
         Co-Lead claim details step relies on it and is deliberately unchanged. -->
    <BaseSelect
      v-if="!searchable"
      v-model="modelValue"
      v-bind="$attrs"
      :options="CASH_CALL_CLAIM_STATE_OPTIONS"
      size="sm"
      label="Loss State"
      rules="required"
    >
      <template v-slot:label>Loss State<span class="text-danger">*</span> </template>

      <template v-slot:first>
        <b-form-select-option :value="undefined"
          >Select</b-form-select-option
        >
      </template>
    </BaseSelect>

    <!-- searchable: an in-page dropdown instead. A native <select>'s option
         list is drawn by the OS and cannot be height-capped in CSS, so the
         ~50-state list runs past the modal. Opted into by the single cash call
         form only (LIQ-1006); pass searchable to get it. -->
    <!-- :value is required: vee-validate 3 can only read a value off a native
         input it wraps. AdvancedDropdown is a custom component, so without
         this the provider always sees empty and reports "required" even once
         a value is chosen. -->
    <ValidationProvider v-else v-slot="{ errors }" name="Loss State" rules="required" :value="value">
      <AdvancedDropdown
        v-model="dropdownValue"
        v-bind="$attrs"
        :options="options"
        :can-add-new="false"
        label="Loss State"
        size="sm"
      >
        <template v-slot:label>Loss State<span class="text-danger">*</span> </template>

        <template v-slot:button-content>
          <template v-if="!!selectedOption">{{ selectedOption.text }}</template>
          <template v-else>Select</template>
        </template>
      </AdvancedDropdown>

      <div v-if="!!errors.length" class="invalid-feedback d-block">
        {{ errors[0] }}
      </div>
    </ValidationProvider>
  </template>

  <script>
  import { defineComponent, computed } from '@vue/composition-api'
  import { ValidationProvider } from 'vee-validate'
  import { CASH_CALL_CLAIM_STATE_OPTIONS } from '@constants'
  import { BaseSelect } from '@components/base'
  import { AdvancedDropdown } from '@components/advanced'

  export default defineComponent({
    name: 'CashCallFormState',
    components: {
      BaseSelect,
      ValidationProvider,
      AdvancedDropdown
    },
    props: {
      value: String,
      // Opt-in only: false keeps the native <select> every existing caller
      // (including the Co-Lead step) already renders.
      searchable: {
        type: Boolean,
        default: false
      }
    },
    emits: ['input'],
    setup(props, { emit }) {

      const displayflex = "displayflex"

      const modelValue = computed({
        get: () => props.value,
        set: (newValue) => emit('input', newValue)
      })

      // AdvancedDropdown is array-valued; this field stays a plain String so
      // the component's contract is identical in both modes.
      const dropdownValue = computed({
        get: () => (props.value ? [props.value] : []),
        set: (newValue) => {
          const picked = Array.isArray(newValue) ? newValue[0] : newValue
          emit('input', picked || '')
        }
      })

      const options = computed(() =>
        CASH_CALL_CLAIM_STATE_OPTIONS.map((opt) =>
          typeof opt === 'object'
            ? { text: opt.text, value: opt.value }
            : { text: opt, value: opt }
        )
      )

      const selectedOption = computed(() => {
        if (!props.value) return null
        return options.value.find((opt) => opt.value === props.value) || null
      })

      return {
        CASH_CALL_CLAIM_STATE_OPTIONS,
        modelValue,
        dropdownValue,
        options,
        selectedOption
      }
    }
  })
  </script>
  <style scoped>
    .advanced-dropdown >>> .dropdown-toggle {
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding-right: 1.75rem;
    }
    .advanced-dropdown >>> .dropdown-menu {
      min-width: 100%;
      /* ~50 states - cap it so the list scrolls inside the modal. */
      max-height: 210px;
    }
  </style>
